import { ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { sortByStartDate } from 'app/events/helpers/sort-by-start-date';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { add, isPast, startOfToday } from 'date-fns';
import {
  CalendarEventResponseStatusEnum,
  GetCalendarEventResponseGQL,
  SearchCalendarEventsGQL,
  SetCalendarEventResponseGQL
} from 'generated/graphql';
import { debounceAllButFirst } from 'helpers';
import { finalize, forkJoin, takeUntil } from 'rxjs';

@Component({
  selector: 'app-add-rsvps-dialog',
  templateUrl: `./add-rsvps.dialog.html`,
  styleUrls: ['./add-rsvps.dialog.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AddRsvpsDialog extends WithDisposableSubject() implements OnInit {
  static open(dialog: MatDialog, config: MatDialogConfig<any>) {
    return dialog.open(AddRsvpsDialog, config);
  }

  today = startOfToday();
  events = [];
  newRsvpEvents = [];
  selectedEvents = [];
  eventsLoading = true;
  isSubmitting = false;
  showTags = false;
  searchCtrl = new FormControl<string>(null);
  dateRangeFilter = new FormGroup({
    start: new FormControl<Date>(this.today),
    end: new FormControl<Date>(add(this.today, { months: 3 })),
  });

  get campaignId() {
    return this._route.snapshot.queryParams['campaignId'] || null;
  }

  constructor(
    public dialogRef: MatDialogRef<AddRsvpsDialog>,
    private _cd: ChangeDetectorRef,
    private _route: ActivatedRoute,
    private _getCalendarEventResponseGQL: GetCalendarEventResponseGQL,
    private _searchCalendarEventsGQL: SearchCalendarEventsGQL,
    private _setCalendarEventResponseGQL: SetCalendarEventResponseGQL,
    @Inject(MAT_DIALOG_DATA) public data: { entityId: string; firstName: string },
  ) {
    super();
    this.updateEvents();
  }

  ngOnInit() {
    this._watchSearchInput();
  }

  updateEvents() {
    this.eventsLoading = true;
    const { start, end } = this.dateRangeFilter.value;
    this._searchCalendarEventsGQL
      .fetch(
        {
          entityId: this.data.entityId,
          campaignId: this.campaignId,
          dateRange: {
            min: start.toISOString(),
            max: end.toISOString(),
          },
          keyword: this.searchCtrl.value,
        },
      )
      .subscribe(({ data }) => {
        this.events = [
          ...data.searchCalendarEvents.nodes,
          // Keep events that were previously selected
          ...this.events.filter(e =>
            this.selectedEvents.includes(e.id) &&
            !data.searchCalendarEvents.nodes.find(e2 => e2.id === e.id)),
        ]
          // Only include events that don't have a response or were declined
          .filter((event) =>
            !event.entityResponse ||
            event.entityResponse.status === CalendarEventResponseStatusEnum.Declined)
          .sort(sortByStartDate);
        this.eventsLoading = false;
      });
  }

  private _watchSearchInput() {
    this.searchCtrl.valueChanges
      .pipe(
        takeUntil(this.disposer),
        debounceAllButFirst(250)
      )
      .subscribe((search) => {
        this.updateEvents();
      });
  }

  onDateChange() {
    // Called on input or datepicker blur
    const { start, end } = this.dateRangeFilter.value;
    if (!start || !end) {
      return;
    }
    this.updateEvents();
  }

  addRsvps(showTags = false) {
    this.isSubmitting = true;

    const mutations = this.selectedEvents.map(eventId => (
      this._setCalendarEventResponseGQL.mutate({
        input: {
          calendarEventId: eventId,
          entityId: this.data.entityId,
          status: isPast(new Date(this.events.find(ev => ev.id === eventId).startDate)) ?
            CalendarEventResponseStatusEnum.Attended : CalendarEventResponseStatusEnum.Rsvp,
        },
      })));

    forkJoin(mutations)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        })
      ).subscribe((results) => {
        if (showTags) {
          this.newRsvpEvents = results.map(({ data }) => ({
            ...data.setCalendarEventResponse.calendarEventResponse.calendarEvent,
            entityResponse: data.setCalendarEventResponse.calendarEventResponse,
          }));
          this.showTags = true;

          // Watch for changes with tags
          this.newRsvpEvents.forEach(event => this.watchForTags(event.entityResponse.id));
        } else {
          this.close();
        }
      });
  }

  watchForTags(responseId: string) {
    this._getCalendarEventResponseGQL.watch({
      campaignId: this.campaignId,
      id: responseId,
    }).valueChanges
      .pipe(
        takeUntil(this.disposer)
      )
      .subscribe(({ data }) => {
        const updatedResponse = data.getCalendarEventResponse;
        // Update newRsvpEvents array
        this.newRsvpEvents = this.newRsvpEvents.map(event =>
          event.entityResponse.id === updatedResponse.id ? {
            ...event,
            entityResponse: updatedResponse,
          } : event);

        this._cd.detectChanges();
      });
  }

  close() {
    this.dialogRef.close();
  }
}
